import { apiClient } from './client';

/**
 * 퇴사 처리(오프보딩) API — backend `modules/hr/offboarding.routes.ts` (인사, migration 131).
 *
 * ★★ **확정해도 사람의 재직 상태는 안 바뀐다** — 그것은 퇴직 **발령**의 일이다(migration 130).
 *    확정 응답의 `next` 가 그 다음 걸음을 알려 준다.
 * ★ 체크리스트의 **이름·순서는 서버가 준다** — 화면이 문구를 따로 갖지 않는다.
 * ★ 옛 서버에는 이 라우트가 없다(404). 부르는 쪽이 실패를 삼키고 화면을 접는다.
 */

export type OffboardingStatus = 'OPEN' | 'DONE' | 'CANCELED';
export type ResignKind = 'VOLUNTARY' | 'CONTRACT_END' | 'DISMISSAL' | 'OTHER';

export interface ChecklistItemDto {
  key: string;
  label: string;
  hint: string;
  required: boolean;
  done: boolean;
  doneAt: string | null;
  doneBy: string | null;
}

export interface OffboardingDto {
  id: string;
  userId: string;
  userName: string | null;
  status: OffboardingStatus;
  resignDate: string;
  resignKind: ResignKind;
  resignKindLabel: string;
  reason: string | null;
  handoverUserId: string | null;
  handoverUserName: string | null;
  checklist: ChecklistItemDto[];
  progress: { done: number; total: number; blocking: string[] };
  canComplete: boolean;
  completedAt: string | null;
  createdAt: string;
}

/** 채워 두지 않으면 **멈추는 자리**. 확정을 막지는 않는다 — 무엇이 멈추는지 말할 뿐이다. */
export interface ImpactHoleDto {
  kind: string;
  label: string;
  count: number;
  detail: string[];
  consequence: string;
}

export async function listOffboardings(status?: OffboardingStatus): Promise<{
  offboardings: OffboardingDto[]; open: number;
}> {
  const { data } = await apiClient.get<{ offboardings: OffboardingDto[]; open: number }>(
    '/hr/offboardings', { params: status ? { status } : undefined },
  );
  return data;
}

export async function getOffboardingImpact(userId: string): Promise<ImpactHoleDto[]> {
  const { data } = await apiClient.get<{ holes: ImpactHoleDto[] }>(`/hr/offboardings/impact/${userId}`);
  return data.holes;
}

export interface StartOffboardingInput {
  userId: string;
  resignDate: string;
  resignKind?: ResignKind;
  reason?: string | null;
  handoverUserId?: string | null;
}

export async function startOffboarding(input: StartOffboardingInput): Promise<OffboardingDto> {
  const { data } = await apiClient.post<{ offboarding: OffboardingDto }>('/hr/offboardings', input);
  return data.offboarding;
}

export async function setOffboardingTask(id: string, key: string, done: boolean): Promise<OffboardingDto> {
  const { data } = await apiClient.patch<{ offboarding: OffboardingDto }>(
    `/hr/offboardings/${id}/task`, { key, done },
  );
  return data.offboarding;
}

/** 확정 — 필수 항목이 다 끝나야 한다. 응답의 `next` 가 «퇴직 발령» 을 가리킨다. */
export async function completeOffboarding(id: string): Promise<{
  offboarding: OffboardingDto; next: { action: string; message: string };
}> {
  const { data } = await apiClient.post<{ offboarding: OffboardingDto; next: { action: string; message: string } }>(
    `/hr/offboardings/${id}/complete`, {},
  );
  return data;
}

export async function cancelOffboarding(id: string): Promise<OffboardingDto> {
  const { data } = await apiClient.post<{ offboarding: OffboardingDto }>(`/hr/offboardings/${id}/cancel`, {});
  return data.offboarding;
}
